import { bodyCopyBold, Button, Divider, Loading } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import ParseHtml from 'html-react-parser';
import styled from 'styled-components';
import { MappedAttributeContainer } from '../../../containers/questions/custom-questions/MappedAttributeContainer';
import ContentConstants from '../../../contentConstants';
import {
	CustomQuestionPropertyUnderValidation,
	ICustomQuestionActionCreators,
	ICustomQuestionEditModel,
	QuestionType,
} from '../../../models';
import { ICustomQuestionState } from '../../../store/CustomQuestionStore';
import { ValidationCollectionHelper } from '../../../utilities';
import { IUserFeatureAccessHelper } from '../../../utilities/FeatureFlags';
import { Col3Col9, LoadingWrapper } from '../../common';
import { CustomResponseOptionsSection, QuestionTextSection } from './';
import { AutoFilledResponseOptions } from './AutoFilledResponseOptions';
import { CustomResponseOptionGroupTabs } from './CustomResponseOptionGroupTabs';

const CustomQuestionWrapper = styled('div')`
	strong {
		${bodyCopyBold}
	}
`;

const DimensionSectionWrapper = styled('div')`
	.dimension-section {
		padding: 0 30px;
	}
`;

export const ResponseOptionsWrapper = styled('div').attrs<{}>({
	className: 'tws-custom-response-options-selector',
})`
	margin: 40px 0;
`;

export const CustomQuestionButtonGroup = styled('div')`
	text-align: right;
	margin-top: 40px;

	.btn {
		margin-left: 10px;
	}
`;

export interface ICustomQuestionContainerProps {
	isDemographic: boolean;
	isFollowUp: boolean;
	fromQuestionModal: boolean;
	parentQuestionId?: number;
	questionId?: number;
	OnFinish: () => void;
	ToggleWarningModal: (openWarningModal: boolean) => void;
}

export type CustomQuestionProps = ICustomQuestionActionCreators &
	ICustomQuestionState &
	ICustomQuestionContainerProps & { userFeatureAccessHelper: IUserFeatureAccessHelper };

export interface ICustomQuestionComponentState {
	validationHelper: ValidationCollectionHelper<CustomQuestionPropertyUnderValidation>;
}

export class CustomQuestion extends React.PureComponent<CustomQuestionProps, ICustomQuestionComponentState> {
	constructor(props: CustomQuestionProps) {
		super(props);

		this.state = {
			validationHelper: new ValidationCollectionHelper<CustomQuestionPropertyUnderValidation>(this.props.validationResults),
		};

		this.save = this.save.bind(this);
		this.resetAndFinish = this.resetAndFinish.bind(this);
		this.getButtonText = this.getButtonText.bind(this);
		this.getDimensionContent = this.getDimensionContent.bind(this);
	}

	public UNSAFE_componentWillMount() {
		this.props.GetCustomQuestion(this.props.isDemographic, this.props.parentQuestionId, this.props.questionId);
		if (!this.props.isDemographic) {
			this.props.GetResponseOptionGroups();
		}
	}

	public UNSAFE_componentWillReceiveProps(nextProps: CustomQuestionProps) {
		if (this.props.saving && !nextProps.saving && !nextProps.unsavedQuestionChanges) {
			const validationHelper = new ValidationCollectionHelper<CustomQuestionPropertyUnderValidation>(nextProps.validationResults);
			this.setState(
				{
					validationHelper,
				},
				() => {
					if (validationHelper.IsValid()) {
						this.props.ResetCustomQuestion();
						this.props.OnFinish();
					}
				},
			);
		}
	}

	public save() {
		this.props.SaveCustomQuestion();
	}

	public resetAndFinish() {
		if (this.props.unsavedQuestionChanges) {
			this.props.ToggleWarningModal(true);
		} else {
			this.props.ResetCustomQuestion();
			this.props.OnFinish();
		}
	}

	public render() {
		const {
			fromQuestionModal,
			isDemographic,
			isFollowUp,
			loading,
			question,
			questionId,
			responseOptionGroupName,
			translations,
			validationResults,
		} = this.props;
		const validationHelper = new ValidationCollectionHelper<CustomQuestionPropertyUnderValidation>(validationResults);
		const questionTextValidation = validationHelper.GetValidationResultForProperty(CustomQuestionPropertyUnderValidation.questionText);
		const responseOptionValidation = validationHelper.GetValidationResultForProperty(CustomQuestionPropertyUnderValidation.responseOptions);
		const attributeNameValidation = validationHelper.GetValidationResultForProperty(CustomQuestionPropertyUnderValidation.attributeName);

		if (loading) {
			return (
				<LoadingWrapper>
					<Loading height={'70px'} width={'70px'} />
				</LoadingWrapper>
			);
		}

		return (
			<CustomQuestionWrapper>
				<QuestionTextSection
					questionId={questionId}
					textValue={this.props.defaultTextValue}
					translations={translations}
					validationResult={questionTextValidation}
					onChange={this.props.HandleQuestionTextChange}
					onTranslationChange={this.props.HandleTranslationChange}
				/>
				{!isDemographic && !isFollowUp && (
					<>
						<DimensionSectionWrapper>
							<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_DIMENSION_INDEX} className='dimension-section'>
								{this.getDimensionContent(question)}
							</Col3Col9>
						</DimensionSectionWrapper>
					</>
				)}

				{isFollowUp && <AutoFilledResponseOptions {...this.props} responseOptionGroupName={responseOptionGroupName} />}

				{!isDemographic && !isFollowUp && (
					<CustomResponseOptionGroupTabs {...this.props} responseOptionValidation={responseOptionValidation} />
				)}
				{isDemographic && !isFollowUp && (
					<>
						<MappedAttributeContainer selected={question.externalAttributeColumnName} validationResult={attributeNameValidation} />
						<CustomResponseOptionsSection
							{...this.props}
							isDemographic={true}
							name={this.props.responseOptionGroupName}
							validationResults={this.props.validationResults}
						/>
					</>
				)}

				<Divider type='dashed' />
				<CustomQuestionButtonGroup>
					{fromQuestionModal && (
						<Button size='lg' color='secondary' id='BackButton' onClick={this.resetAndFinish}>
							{ContentConstants.BUTTONS_BACK_TO_QUESTIONS}
						</Button>
					)}
					<Button size='lg' color='primary' onClick={this.save} disabled={this.saveDisabled()}>
						{this.getButtonText()}
					</Button>
				</CustomQuestionButtonGroup>
			</CustomQuestionWrapper>
		);
	}

	private saveDisabled() {
		if (this.props.saving) {
			return true;
		}

		if (this.props.questionId !== undefined && this.props.questionId > 0 && !this.props.unsavedQuestionChanges) {
			return true;
		}
		if (
			this.props.isDemographic ||
			this.props.questionType === QuestionType.OpenEnded ||
			this.props.questionType === QuestionType.TextBox
		) {
			return false;
		}
	}

	private getButtonText() {
		if (this.props.questionId && this.props.questionId > 0) {
			return ContentConstants.BUTTONS_UPDATE_QUESTION;
		} else {
			return ContentConstants.BUTTONS_CREATE_QUESTION;
		}
	}

	private getDimensionContent(question: ICustomQuestionEditModel) {
		if (question.dimensions && question.dimensions.length > 0) {
			return <p>{question.dimensions.map((d) => d.name).join(', ')}</p>;
		} else {
			return <>{ParseHtml(ContentConstants.CUSTOM_QUESTION_DEFAULT_DIMENSION)}</>;
		}
	}
}
